/**
 * Persian/Farsi utility functions for number formatting, date formatting, and currency display
 */

/**
 * Convert English/Arabic numerals to Persian numerals
 * @param num - Number or string to convert
 * @returns String with Persian numerals
 */
export const toPersianNumber = (num: number | string): string => {
  const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
  return String(num).replace(/\d/g, (digit) => persianDigits[parseInt(digit)]);
};

/**
 * Format a number as Persian currency (Toman)
 * @param amount - Amount in Toman
 * @returns Formatted string with Persian numerals and currency symbol
 */
export const formatCurrency = (amount: number): string => {
  const formatted = amount.toLocaleString('fa-IR');
  return `${toPersianNumber(formatted)} تومان`;
};

/**
 * Format a date using Persian (Jalali) calendar
 * @param date - Date object or ISO string
 * @returns Formatted Persian date string
 */
export const formatDate = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  // Use Intl.DateTimeFormat with Persian calendar
  const formatter = new Intl.DateTimeFormat('fa-IR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    calendar: 'persian',
  });
  
  return formatter.format(dateObj);
};

/**
 * Format a date and time using Persian (Jalali) calendar
 * @param date - Date object or ISO string
 * @returns Formatted Persian date and time string
 */
export const formatDateTime = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  const formatter = new Intl.DateTimeFormat('fa-IR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    calendar: 'persian',
  });
  
  return formatter.format(dateObj);
};

/**
 * Format a number with thousands separator in Persian
 * @param num - Number to format
 * @returns Formatted string with Persian numerals
 */
export const formatNumber = (num: number): string => {
  const formatted = num.toLocaleString('fa-IR');
  return toPersianNumber(formatted);
};

/**
 * Format decimal number with specified precision
 * @param num - Number to format
 * @param precision - Number of decimal places (default: 1)
 * @returns Formatted string with Persian numerals
 */
export const formatDecimal = (num: number, precision: number = 1): string => {
  const formatted = num.toFixed(precision);
  return toPersianNumber(formatted);
};
